import React from "react";
import { Link } from "react-router-dom";
import { Camera, Banknote, Headphones, ArrowRight, CheckCircle2 } from "lucide-react";
import { Button } from "../components/ui/button";
import { useAuth } from "../lib/auth";

const STEPS = [
  { icon: Camera, title: "List your car", body: "Add photos, specs and your price — takes under 5 minutes." },
  { icon: Headphones, title: "Get genuine leads", body: "Buyers WhatsApp you directly. No middlemen, no commissions." },
  { icon: Banknote, title: "Close the deal", body: "Meet, test drive, sell — and we help with paperwork." },
];

export default function Sell() {
  const { user } = useAuth();

  return (
    <div data-testid="sell-page">
      <div className="bg-[#F97316] text-[#0A192F]" data-testid="sell-dealer-banner">
        <div className="max-w-7xl mx-auto px-4 md:px-8 py-4 flex flex-wrap items-center justify-between gap-3">
          <p className="font-semibold text-sm sm:text-base">
            Own a pre-owned car showroom? List your entire stock free with 0% brokerage and get direct WhatsApp leads.
          </p>
          <Link
            to="/dealer-onboarding"
            className="inline-flex items-center gap-2 bg-[#0A192F] text-white font-bold text-sm rounded-lg px-4 py-2 hover:bg-[#0A192F]/90 transition-colors"
            data-testid="sell-dealer-banner-cta"
          >
            Register as Dealer <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
      </div>
      <section className="bg-[#0A192F] text-white relative overflow-hidden">
        <img
          src="https://images.unsplash.com/photo-1494976388531-d1058494cdd8?w=1600"
          alt="Sell your car"
          className="absolute inset-0 w-full h-full object-cover opacity-30"
        />
        <div className="absolute inset-0 gj-hero-gradient" />
        <div className="relative max-w-7xl mx-auto px-4 md:px-8 py-20 md:py-28">
          <div className="max-w-2xl">
            <div className="gj-divider-gold mb-4" />
            <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tight leading-[1.05]">
              Sell your car for the <span className="text-[#F97316]">best price</span> in West Bengal.
            </h1>
            <p className="mt-5 text-white/80 max-w-xl">
              List free for 30 days. Reach 10,000+ serious buyers from Kolkata,
              Howrah, Durgapur and beyond.
            </p>
            <div className="mt-8 flex flex-wrap gap-3">
              {user && (user.role === "dealer" || user.role === "admin") ? (
                <Link to="/dealer">
                  <Button className="bg-[#F97316] hover:bg-[#EA580C] text-[#0A192F] font-bold rounded-lg px-6 py-6" data-testid="sell-go-dashboard">
                    Go to dashboard <ArrowRight className="ml-2 w-4 h-4" />
                  </Button>
                </Link>
              ) : (
                <Link to="/dealer-onboarding">
                  <Button className="bg-[#F97316] hover:bg-[#EA580C] text-[#0A192F] font-bold rounded-lg px-6 py-6" data-testid="sell-start-listing">
                    Start listing — Free <ArrowRight className="ml-2 w-4 h-4" />
                  </Button>
                </Link>
              )}
              <Link to="/cars">
                <Button variant="outline" className="border-white/40 text-white hover:bg-white/10 hover:text-white rounded-lg px-6 py-6 bg-transparent">
                  Browse marketplace
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-4 md:px-8 py-16 md:py-24">
        <div className="text-center max-w-2xl mx-auto mb-12">
          <div className="gj-divider-gold mx-auto mb-4" />
          <h2 className="font-heading text-3xl sm:text-4xl font-bold text-[#0A192F]">
            How it works
          </h2>
          <p className="text-[#4A5568] mt-2">
            Selling a car in Bengal has never been this simple.
          </p>
        </div>
        <div className="grid md:grid-cols-3 gap-6">
          {STEPS.map((s, i) => (
            <div key={s.title} className="bg-white border border-[#0A192F]/10 rounded-xl p-6 gj-shadow-card">
              <div className="text-xs uppercase tracking-[0.18em] text-[#F97316] font-bold">Step {i + 1}</div>
              <s.icon className="w-7 h-7 text-[#0A192F] mt-3" strokeWidth={1.5} />
              <div className="font-heading text-xl font-bold text-[#0A192F] mt-3">{s.title}</div>
              <p className="text-sm text-[#4A5568] mt-2 leading-relaxed">{s.body}</p>
            </div>
          ))}
        </div>
      </section>

      <section className="bg-[#F3F0EA]/60">
        <div className="max-w-7xl mx-auto px-4 md:px-8 py-16 md:py-24 grid md:grid-cols-2 gap-10 items-center">
          <div>
            <div className="gj-divider-gold mb-4" />
            <h2 className="font-heading text-3xl sm:text-4xl font-bold text-[#0A192F]">
              Why dealers choose <span className="text-[#F97316]">Gaddi Junction</span>
            </h2>
            <ul className="mt-6 space-y-3 text-[#0A192F]">
              {[
                "Zero commission, transparent pricing",
                "WhatsApp leads directly to your phone",
                "Verified dealer badge for higher trust",
                "Featured slots to boost visibility",
                "Local Bengal-focused buyer base",
              ].map((t) => (
                <li key={t} className="flex items-start gap-3">
                  <CheckCircle2 className="w-5 h-5 text-[#F97316] mt-0.5" />
                  <span>{t}</span>
                </li>
              ))}
            </ul>
            <Link to="/dealer-onboarding" className="inline-block mt-8">
              <Button className="bg-[#0A192F] hover:bg-[#112240] text-white rounded-lg px-6 py-6" data-testid="sell-register-dealer">
                Register as Dealer
              </Button>
            </Link>
          </div>
          <div className="aspect-[4/3] rounded-2xl overflow-hidden gj-shadow-card">
            <img
              src="https://images.unsplash.com/photo-1731202892400-0faa02bc67f0?w=1200"
              alt="Happy family"
              className="w-full h-full object-cover"
            />
          </div>
        </div>
      </section>
    </div>
  );
}
